import { DateTime } from 'luxon';
import { CALENDAR_DEFAULT_USER_FILTER } from '../defaults';
import { CalendarEventsFilterRanges } from '../enums/CalendarEventsFilterRanges';
import {
    CalendarEvent,
    CalendarEventFormState,
    CreateCalendarEventRequest,
    UpdateCalendarEventRequest,
} from '../types/CalendarEvent';
import { CalendarUserFilterItem } from '../types/CalendarUserFilter';
import { validateEmailInput, validateStringInput } from './formValidationHelpers';
import {
    isCalendarEventFuture,
    isCalendarEventNext30Days,
    isCalendarEventNext7Days,
    isCalendarEventPast,
    isCalendarEventPast30Days,
    isCalendarEventPast7Days,
    isCalendarEventToday,
} from './functionalFilters';

export function mapCalendarEventFormStateRequest(
    contactId: number,
    { ...formState }: CalendarEventFormState,
): CreateCalendarEventRequest {
    return {
        contactId: contactId,
        startDate: formState.dtpStartDate,
        endDate: formState.dtpEndDate,
        title: validateStringInput('txtTitle', formState.txtTitle),
        description: formState.txtDescription,
        owner: formState.userName,
        allDay: formState.chkAllDay,
        location: formState.txtLocation,
        status: formState.ddlStatus,
        guestEmails: formState.ddlGuestEmails.map(email =>
            validateEmailInput('ddlGuestEmails', email),
        ),
        addContactAsGuest: formState.addContactAsGuest,
        generateMeetLink: formState.chkGenerateMeetLink,
        generateZoomLink: formState.chkGenerateZoomLink,
    };
}

export function mapCalendarEventRequest({
    ...calendarEvent
}: CalendarEvent): UpdateCalendarEventRequest {
    return { ...calendarEvent };
}

/**
 * getFilteredEvents
 *
 * @description
 * filters calendar events based on the speicified range filter
 */
export function getFilteredEvents(
    events: CalendarEvent[],
    rangeFilter: CalendarEventsFilterRanges,
) {
    switch (rangeFilter) {
        case CalendarEventsFilterRanges.all:
            return events;
        case CalendarEventsFilterRanges.today:
            return events.filter(isCalendarEventToday);
        case CalendarEventsFilterRanges.next7days:
            return events.filter(isCalendarEventNext7Days);
        case CalendarEventsFilterRanges.next30days:
            return events.filter(isCalendarEventNext30Days);
        case CalendarEventsFilterRanges.allfuture:
            return events.filter(isCalendarEventFuture);
        case CalendarEventsFilterRanges.past7days:
            return events.filter(isCalendarEventPast7Days);
        case CalendarEventsFilterRanges.past30days:
            return events.filter(isCalendarEventPast30Days);
        case CalendarEventsFilterRanges.allpast:
            return events.filter(isCalendarEventPast);
    }
}

export function mapUserToCalendarUserFilter(userName: string) {
    return {
        label: userName,
        userName: userName,
    } as CalendarUserFilterItem;
}

export function alphabeticCompare(a: string, b: string) {
    const nameA = a.toLowerCase(),
        nameB = b.toLowerCase();
    if (nameA < nameB)
        //sort string ascending
        return -1;
    if (nameA > nameB) return 1;
    return 0; //default return value (no sorting)
}

export function getCalendarUserFilters(clientUsers: string[]) {
    return [
        CALENDAR_DEFAULT_USER_FILTER,
        ...clientUsers.map(mapUserToCalendarUserFilter),
    ] as CalendarUserFilterItem[];
}

/**
 * Intelligently formats calendar event start & end dates.
 * Single day with duration: Jan 1, 10:00 AM
 * @param calendarEvent
 * @returns
 */
export function formatStartEndDates(calendarEvent: CalendarEvent) {
    const isMultiDay =
        calendarEvent.endDate &&
        calendarEvent.startDate.startOf('day') < calendarEvent.endDate.startOf('day');

    if (isMultiDay && calendarEvent.allDay)
        return `${calendarEvent.startDate.toFormat('LLL d')} - ${calendarEvent.endDate?.toFormat(
            'LLL d',
        )}`;

    if (isMultiDay && !calendarEvent.allDay)
        return `${calendarEvent.startDate.toFormat(
            'LLL d, h:mm a',
        )} - ${calendarEvent.endDate?.toFormat('LLL d, h:mm a')}`;

    if (!isMultiDay && calendarEvent.allDay) return calendarEvent.startDate.toFormat('LLL d');

    if (!isMultiDay && !calendarEvent.allDay && calendarEvent.endDate)
        return `${calendarEvent.startDate.toFormat(
            'LLL d, h:mm a',
        )} - ${calendarEvent.endDate?.toFormat('h:mm a')}`;

    if (!isMultiDay && !calendarEvent.allDay && !calendarEvent.endDate)
        return `${calendarEvent.startDate.toFormat('LLL d, h:mm a')}`;

    return `You found a bug! ${calendarEvent.eventId}`;
}

export function formatCalendarEventDates(event: {
    startDate: DateTime;
    endDate?: DateTime;
    allDay: boolean;
}): string {
    // Determine if the dates fall on the same day.
    const isSameDayRange = event.endDate ? event.startDate.hasSame(event.endDate, 'day') : true;

    // Determine if the event is a "full day" event.
    const isFullDayEvent =
        event.allDay ||
        (event.startDate.startOf('day').equals(event.startDate) &&
            event.endDate?.startOf('day').equals(event.endDate));

    // Format the date strings.
    const startDateString = event.startDate.toLocaleString(DateTime.DATE_FULL);
    const endDateString = event.endDate ? event.endDate.toLocaleString(DateTime.DATE_FULL) : '';

    let formattedTimeRange = '';

    if (!isFullDayEvent) {
        const startTimeString = event.startDate.toLocaleString(DateTime.TIME_SIMPLE);
        const endTimeString = event.endDate?.toLocaleString(DateTime.TIME_SIMPLE) || '';
        formattedTimeRange = `, ${startTimeString} - ${endTimeString}`;
    }

    if (isSameDayRange) {
        // Return the formatted string for an event on a single day.
        return `${startDateString}${formattedTimeRange}`;
    } else if (
        event.endDate?.startOf('day').diff(event.startDate.startOf('day'), 'days').toObject()
            .days === 1
    ) {
        // Return the formatted string for an event that spans two days.
        return `${startDateString}${formattedTimeRange} - ${endDateString}${formattedTimeRange}`;
    } else {
        // Return the formatted string for an event that spans more than two days.
        return `${startDateString}${formattedTimeRange} - ${event.endDate?.toLocaleString(
            DateTime.DATETIME_SHORT,
        )}`;
    }
}
